import { MultiSelectComboField } from '@backoffice-frontend/forms'
import { XIcon } from '@moia-dev/pace-icons'
import {
  Button,
  DateRangePicker,
  Heading,
  IconButton,
  Text,
} from '@moia-dev/pace-web'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import type { HeatmapFilterOptions, HeatmapLayerType } from './HeatmapLayer'

export type HeatmapFilters = {
  eventTypes: string[]
  initiators: string[]
  disengagementReasons: string[]
  softwareVersions: string[]
  startTimestampMs: number | null
  endTimestampMs: number | null
}

export const EMPTY_HEATMAP_FILTERS: HeatmapFilters = {
  eventTypes: [],
  initiators: [],
  disengagementReasons: [],
  softwareVersions: [],
  startTimestampMs: null,
  endTimestampMs: null,
}

export const hasActiveFilters = (filters: HeatmapFilters): boolean =>
  filters.eventTypes.length > 0 ||
  filters.initiators.length > 0 ||
  filters.disengagementReasons.length > 0 ||
  filters.softwareVersions.length > 0 ||
  filters.startTimestampMs != null ||
  filters.endTimestampMs != null

// ─── Modal ────────────────────────────────────────────────────────────────────

type HeatmapFilterModalProps = {
  heatmapType: HeatmapLayerType
  currentFilters: HeatmapFilters
  filterOptions: HeatmapFilterOptions | null
  onSave: (filters: HeatmapFilters) => void
  onClose: () => void
}

const LABEL: Record<NonNullable<HeatmapLayerType>, string> = {
  pce: 'Potential Critical Events',
  disengagements: 'Disengagements',
  geonetCoverage: 'Geonet Coverage',
}

const FilterRow = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => (
  <div
    css={theme => ({
      display: 'flex',
      flexDirection: 'column',
      gap: theme.space.Space2,
    })}
  >
    <Text size="sm" weight="regular">
      {label}
    </Text>
    {children}
  </div>
)

export const HeatmapFilterModal = ({
  heatmapType,
  currentFilters,
  filterOptions,
  onSave,
  onClose,
}: HeatmapFilterModalProps) => {
  const [draft, setDraft] = useState<HeatmapFilters>(currentFilters)
  const [swQuery, setSwQuery] = useState('')
  const [etQuery, setEtQuery] = useState('')
  const [initQuery, setInitQuery] = useState('')
  const [reasonQuery, setReasonQuery] = useState('')

  if (!heatmapType) return null

  const title = `${LABEL[heatmapType]} Filters`
  const hasChanges = JSON.stringify(draft) !== JSON.stringify(currentFilters)

  const dateRangeValue = {
    from:
      draft.startTimestampMs != null
        ? new Date(draft.startTimestampMs)
        : undefined,
    to:
      draft.endTimestampMs != null ? new Date(draft.endTimestampMs) : undefined,
  }

  const filterByQuery = (options: string[], query: string) =>
    options.filter(o => o.toLowerCase().includes(query.toLowerCase()))

  return createPortal(
    <div
      css={{
        position: 'fixed',
        inset: 0,
        // Sit below the pace-web combobox dropdown (a FloatingPortal fixed at
        // z-index 9999) so the MultiSelectCombobox option lists render on top of
        // the modal instead of behind it, while still covering the map overlays.
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      }}
      onClick={e => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        css={theme => ({
          width: 460,
          backgroundColor: theme.semantic.ColorSurfaceDefault,
          borderRadius: theme.radius.RadiusLarge,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
        })}
      >
        <div
          css={theme => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: `${theme.space.Space3} ${theme.space.Space4}`,
            borderBottom: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
            flexShrink: 0,
          })}
        >
          <Heading size="sm">{title}</Heading>
          <IconButton
            aria-label="Close"
            size="compact"
            variant="plain"
            icon={<XIcon />}
            onClick={onClose}
          />
        </div>
        <div
          css={theme => ({
            display: 'flex',
            flexDirection: 'column',
            gap: theme.space.Space4,
            padding: theme.space.Space4,
            // Add chevron rotation transition missing from pace-web MultiSelectCombobox
            'button[class*="comboButton"] svg': {
              transition: 'transform 150ms ease',
            },
          })}
        >
          {/* Software Versions — all types */}
          <MultiSelectComboField<string>
            label="Software Versions"
            values={draft.softwareVersions}
            options={filterByQuery(
              filterOptions?.softwareVersions ?? [],
              swQuery,
            )}
            query={swQuery}
            onQueryChange={setSwQuery}
            optionToId={o => o}
            renderOption={o => o ?? ''}
            onChange={softwareVersions =>
              setDraft(d => ({ ...d, softwareVersions }))
            }
            closeOnSelect
          />

          {/* PCE: event types */}
          {heatmapType === 'pce' && (
            <MultiSelectComboField<string>
              label="Event Types"
              values={draft.eventTypes}
              options={filterByQuery(filterOptions?.eventTypes ?? [], etQuery)}
              query={etQuery}
              onQueryChange={setEtQuery}
              optionToId={o => o}
              renderOption={o => o ?? ''}
              onChange={eventTypes => setDraft(d => ({ ...d, eventTypes }))}
              closeOnSelect
            />
          )}

          {/* Disengagements: initiators + reasons */}
          {heatmapType === 'disengagements' && (
            <>
              <MultiSelectComboField<string>
                label="Initiators"
                values={draft.initiators}
                options={filterByQuery(
                  filterOptions?.initiators ?? [],
                  initQuery,
                )}
                query={initQuery}
                onQueryChange={setInitQuery}
                optionToId={o => o}
                renderOption={o => o ?? ''}
                onChange={initiators => setDraft(d => ({ ...d, initiators }))}
                closeOnSelect
              />
              <MultiSelectComboField<string>
                label="Disengagement Reasons"
                values={draft.disengagementReasons}
                options={filterByQuery(
                  filterOptions?.disengagementReasons ?? [],
                  reasonQuery,
                )}
                query={reasonQuery}
                onQueryChange={setReasonQuery}
                optionToId={o => o}
                renderOption={o => o ?? ''}
                onChange={disengagementReasons =>
                  setDraft(d => ({ ...d, disengagementReasons }))
                }
                closeOnSelect
              />
            </>
          )}

          {/* Date range — PCE and disengagements only */}
          {heatmapType !== 'geonetCoverage' && (
            <FilterRow label="Date Range">
              <DateRangePicker
                value={dateRangeValue}
                onChange={range => {
                  setDraft(d => ({
                    ...d,
                    startTimestampMs: range?.from?.getTime() ?? null,
                    endTimestampMs: range?.to?.getTime() ?? null,
                  }))
                }}
              />
            </FilterRow>
          )}
        </div>
        <div
          css={theme => ({
            display: 'flex',
            justifyContent: 'flex-end',
            gap: theme.space.Space2,
            padding: `${theme.space.Space3} ${theme.space.Space4}`,
            borderTop: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
          })}
        >
          <Button
            variant="secondary"
            label="Reset"
            onClick={() => setDraft(EMPTY_HEATMAP_FILTERS)}
          />
          <Button
            label="Apply"
            onClick={() => {
              onSave(draft)
              onClose()
            }}
            disabled={!hasChanges}
          />
        </div>
      </div>
    </div>,
    document.body,
  )
}
